import {PERMISSION_TYPE} from '../../../Permissions';
import {hasFeatureFlag, isQuotesEnabled, isOpportunitiesEnabled} from '../../shared/util/FeatureUtil';
import {hasModule} from '../../shared/util/ModuleUtil';
import {isTicketingAvailable} from '../../shared/util/ticketingAvailability';
import {isStreamAvailable} from '../../shared/util/streamAvailability';
import {FEATURE_FLAGS, MODULE_TYPES} from '../../../constants';

//**************************************************************
// If you add a ternary/inline if to a permission name or description
// then the you need to change the field to a function otherwise
// it will not work properly
//**************************************************************

const programText = () => (hasModule(MODULE_TYPES.PROGRAMS_PLUS) ? 'program' : 'project group');
const programsText = () => (hasModule(MODULE_TYPES.PROGRAMS_PLUS) ? 'programs' : 'project groups');

export const CREATE_PERSON = {
	name: 'Invite new team members',
	permissions: [PERMISSION_TYPE.PERSON_CREATE],
	description: 'Ability to invite new users to the account (this might add additional seats to your subscription).',
};

export const SCHEDULING_ACCESS = {
	name: 'View Schedule People and Schedule Projects',
	permissions: [PERMISSION_TYPE.SCHEDULING_ACCESS],
};

export const SEE_MANAGE_INSIGHTS = {
	name: 'See and manage reports',
	permissions: [
		PERMISSION_TYPE.INSIGHTS_READ,
		PERMISSION_TYPE.INSIGHTS_CREATE,
		PERMISSION_TYPE.INSIGHTS_UPDATE,
		PERMISSION_TYPE.INSIGHTS_DELETE,
	],
	description: 'Access to the reports tab and ability to create, edit & delete reports.',
};

export const CREATE_UPDATE_CLIENT = {
	name: 'Create and update client info',
	permissions: [PERMISSION_TYPE.CLIENT_CREATE, PERMISSION_TYPE.CLIENT_UPDATE],
};

// This group is part of Manage projects for now.
export const MANAGE_PROJECT_ASSIGNMENT = {
	name: 'Assign and remove people to/from projects',
	permissions: [
		PERMISSION_TYPE.PROJECT_PERSON_CREATE,
		PERMISSION_TYPE.PROJECT_PERSON_UPDATE,
		PERMISSION_TYPE.PROJECT_PERSON_DELETE,
	],
};

export const MANAGE_WORKFLOW_COLUMN = {
	name: 'Manage workflow columns',
	permissions: [
		PERMISSION_TYPE.STATUS_COLUMN_CREATE,
		PERMISSION_TYPE.STATUS_COLUMN_UPDATE,
		PERMISSION_TYPE.STATUS_COLUMN_DELETE,
	],
	description: 'Ability to create, edit & delete new workflow columns in projects.',
};

// "Manage tasks" is a container row: it holds no permission of its own, only the union of its
// four children's, so ticking it grants them all and it shows as checked only while they all are.
// Each child stays individually tickable underneath.
// Children carry `nested` so the permissions table indents them under the parent.
export const DELETE_TASKS = {
	name: 'Delete tasks',
	permissions: [PERMISSION_TYPE.TASK_DELETE],
	description:
		'Ability to delete any task, including top-level tasks. Profiles with "Edit tasks" can already delete subtasks without this.',
	nested: true,
};

export const MANAGE_SPRINTS = {
	name: 'Manage sprints',
	permissions: [PERMISSION_TYPE.SPRINT_CREATE, PERMISSION_TYPE.SPRINT_UPDATE, PERMISSION_TYPE.SPRINT_DELETE],
	description: 'Ability to create, edit & delete sprints in projects',
};

export const MANAGE_PHASES = {
	name: 'Manage phases',
	permissions: [PERMISSION_TYPE.PHASE_CREATE, PERMISSION_TYPE.PHASE_UPDATE, PERMISSION_TYPE.PHASE_DELETE],
	description: 'Ability to create, edit & delete phases in projects.',
};

export const EDIT_TASKS = {
	name: 'Edit tasks',
	permissions: [PERMISSION_TYPE.TASK_UPDATE],
	// A getter, not a literal: the sentence about workflow columns refers to the "Change task
	// status" row, which is not rendered until task_status_permission is on. While that flag is
	// off, status genuinely is unrestricted, so the original wording is the true one — pointing an
	// admin at a permission that is absent from their grid is worse than saying nothing.
	//
	// A getter rather than a function so the group keeps its object identity; several callers and
	// tests compare these groups by reference.
	get description() {
		return (
			'Ability to edit: name, description, priority, labels, assignees, role, owner, followers, start/due date, estimate, recurrence, and the bug and billable flags. The Approved flag is also governed by this permission, but additionally requires "Manage phases". Also includes reassigning a task\'s phase, milestone, or sprint, and creating or re-nesting subtasks and dependencies — current values and existing subtasks/dependencies are always visible; only the add/change actions require this permission. Also includes deleting subtasks; deleting a top-level task requires "Delete tasks". ' +
			(hasFeatureFlag(FEATURE_FLAGS.TASK_STATUS_PERMISSION)
				? 'Moving a task to a different workflow column is governed separately by "Change task status". Not restricted: comments, '
				: 'Not restricted: status changes, comments, ') +
			'file attachments, the Blocked flag, Favorite/star, manual-mode time left and progress, and custom fields. Moving a task between projects requires "Manage projects".'
		);
	},
	nested: true,
};

export const CREATE_TASKS = {
	name: 'Create tasks',
	permissions: [PERMISSION_TYPE.TASK_CREATE],
	description:
		'Ability to create new tasks in a project, including subtasks. Creating a subtask is also allowed by "Edit tasks", so a profile needs only one of the two.',
	nested: true,
};

export const CHANGE_TASK_STATUS = {
	name: 'Change task status',
	permissions: [PERMISSION_TYPE.TASK_STATUS_CHANGE],
	description:
		'Ability to move a task to a different workflow column — by dragging a card on the task board, by the status dropdown in the task modal, on subtask rows and in the task tables, or in bulk from the "Move to status" action. Independent of "Edit tasks": a profile can be allowed to move work along without being allowed to reshape it, or the reverse. Creating a task in a column is governed by "Create tasks", and creating or deleting the columns themselves by "Manage workflow columns".',
	nested: true,
};

export const MANAGE_TASKS = {
	name: 'Manage tasks',
	// A getter, not a literal, because "Change task status" is shown only while
	// task_status_permission is on, and its three siblings always. The union must contain only the
	// children whose rows actually render, or unticking the container strips a permission the admin
	// has no row to restore.
	//
	// Concretely, with task_status_permission off, a static four-permission union would let an
	// admin untick "Manage tasks", lose TASK_STATUS_CHANGE with it, re-tick Create/Edit/Delete, and
	// be left with the status grant silently gone. It would surface only once the status flag turned
	// enforcement on, as exactly the capability loss the backfill exists to prevent. Keeping the
	// union in step with the rendered rows upholds the rule PermissionsUtil states for this: a
	// gate hides a row, it does not revoke what it grants.
	get permissions() {
		return [
			...CREATE_TASKS.permissions,
			...EDIT_TASKS.permissions,
			...(hasFeatureFlag(FEATURE_FLAGS.TASK_STATUS_PERMISSION) ? CHANGE_TASK_STATUS.permissions : []),
			...DELETE_TASKS.permissions,
		];
	},
	description:
		'Full control over tasks. Enabling this grants everything nested beneath it; switch individual rows off to restrict a profile to part of it.',
};

// Changed to a function to make the inline if work
export const ACCESS_ALL_PROJECTS = () => {
	return {
		name: `Access all projects${hasModule(MODULE_TYPES.PROGRAMS) ? ' and ' + programsText() : ''}`,
		permissions: [PERMISSION_TYPE.PROJECTS_READ_ALL, PERMISSION_TYPE.PROJECTS_READ_ALL_VIEW_ONLY],
		description: `Access to all projects${
			hasModule(MODULE_TYPES.PROGRAMS) ? ' and ' + programsText() : ''
		} on the account, independent of being part of the project${
			hasModule(MODULE_TYPES.PROGRAMS) ? ' or ' + programText() : ''
		} team.`,
	};
};

export const READ_ALL_PROJECTS = {
	name: 'Read data access for all projects and programs',
	permissions: [PERMISSION_TYPE.PROJECTS_READ_ALL_VIEW_ONLY],
	description:
		'Makes it possible to view data from all projects and programs, without requiring or granting edit access to all projects and programs.',
};

export const SEE_MANAGE_BASELINE = {
	name: 'See and manage Baseline',
	permissions: [
		PERMISSION_TYPE.BASELINE_READ,
		PERMISSION_TYPE.BASELINE_CREATE,
		PERMISSION_TYPE.BASELINE_UPDATE,
		PERMISSION_TYPE.BASELINE_DELETE,
	],
};

export const MANAGE_COMMENTS_ALL = {
	name: "Edit another person's comment",
	permissions: [PERMISSION_TYPE.COMMENT_UPDATE_ALL, PERMISSION_TYPE.COMMENT_DELETE_ALL],
	description: 'Ability to edit & delete other users’ comments on tasks.',
};

export const SEE_MANAGE_INVOICES = {
	name: 'See and manage Invoices',
	permissions: [
		PERMISSION_TYPE.INVOICE_READ,
		PERMISSION_TYPE.INVOICE_CREATE,
		PERMISSION_TYPE.INVOICE_UPDATE,
		PERMISSION_TYPE.INVOICE_DELETE,
	],
};

export const MANAGE_TIME_OFF = {
	name: "Manage other person's time-off",
	permissions: [PERMISSION_TYPE.MANAGE_TIME_OFF],
	description:
		'Access to the company timesheets, ' +
		'ability to view time-offs of other users and to create, ' +
		'edit, approve and reject time-off ' +
		'(only if time-off approval is enabled).',
};

export const MANAGE_TIME_REGISTRATIONS_ALL = {
	name: "Manage other person's time registrations",
	permissions: [
		PERMISSION_TYPE.TIME_REGISTRATION_CREATE_ALL,
		PERMISSION_TYPE.TIME_REGISTRATION_UPDATE_ALL,
		PERMISSION_TYPE.TIME_REGISTRATION_DELETE_ALL,
		...MANAGE_TIME_OFF.permissions,
	],

	description:
		'Access to the company timesheets, ' +
		'and ability to read, create, ' +
		'edit, and delete time entries for other users. ' +
		'Access to locking, approving, and rejecting time sheets for Project Owners and Admins (if Time Lock / Time Approval functionality is enabled).',
};

export const MANAGE_ALLOCATION = {
	name: 'Manage allocations',
	permissions: [
		PERMISSION_TYPE.ALLOCATION_CREATE,
		PERMISSION_TYPE.ALLOCATION_UPDATE,
		PERMISSION_TYPE.ALLOCATION_DELETE,
		...SCHEDULING_ACCESS.permissions,
		...SEE_MANAGE_INSIGHTS.permissions,
	],
};

export const VIEW_FINANCIAL_INFORMATION_REVENUE = {
	name: 'View financial information, revenue only',
	permissions: [
		PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE,
		PERMISSION_TYPE.RATE_CARDS_READ,
		PERMISSION_TYPE.RATE_CARDS_UPDATE,
		...SEE_MANAGE_BASELINE.permissions,
		...SEE_MANAGE_INVOICES.permissions,
	],
	description:
		'Access to all financial features including Baseline, Budget, ' +
		'Periods & the Project Portfolio Report - viewing only revenue',
};

export const ALL_TIMELINES_VIEW_ONLY = {
	name: 'View All Timelines Page',
	permissions: [PERMISSION_TYPE.ALL_TIMELINES_VIEW_ONLY],
	description: 'Ability to view the All Timelines page. Does not entail the ability to make changes.',
};

export const PEOPLE_SCHEDULE_VIEW_ONLY = {
	name: 'View People Schedule Page',
	permissions: [PERMISSION_TYPE.PEOPLE_SCHEDULE_VIEW_ONLY],
	description: 'Ability to view the People Schedule page. Does not entail the ability to make changes.',
};

export const DEMAND_VIEW_ONLY = {
	name: 'View Demand Page',
	permissions: [PERMISSION_TYPE.DEMAND_VIEW_ONLY],
	description: 'Ability to view the Demand page. Does not entail the ability to make changes.',
};

export const CAPACITY_OVERVIEW_VIEW_ONLY = {
	name: 'View Capacity Overview Page',
	permissions: [PERMISSION_TYPE.CAPACITY_OVERVIEW_VIEW_ONLY],
	description: 'Ability to view the Capacity Overview page. Does not entail the ability to make changes.',
};

export const AI_ACCESS_GROUP = {
	name: 'Use AI features',
	permissions: [PERMISSION_TYPE.AI_ACCESS],
	description:
		'Ability to use the AI features the account has enabled: the Nova chat, the AI report builder, project planning from a statement of work, and Nova agents.',
};

export const MANUAL_REVENUE_RECOGNITION = {
	name: 'Manual revenue recognition',
	permissions: [PERMISSION_TYPE.MANUAL_REVENUE_RECOGNITION],
	description: 'Gives access to manual revenue recognition',
};

export const VIEW_FINANCIAL_INFORMATION = {
	name: 'View financial information',
	permissions: [PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION, ...VIEW_FINANCIAL_INFORMATION_REVENUE.permissions],
	description:
		'Access to all financial features including Baseline, ' +
		'Budget, Periods & the Project Portfolio Report - viewing revenue, cost & profit',
};

export const APPROVE_ALLOCATION = {
	name: 'Approve allocations',
	permissions: [PERMISSION_TYPE.ALLOCATION_APPROVE],
	description: 'Ability to change soft allocations to hard allocations and staff placeholders.',
};

export const MANAGE_PROJECTS = {
	name: 'Manage projects',
	permissions: [
		PERMISSION_TYPE.PROJECTS_CREATE,
		PERMISSION_TYPE.PROJECTS_UPDATE,
		PERMISSION_TYPE.PROJECTS_DELETE,
		...CREATE_UPDATE_CLIENT.permissions,
		...MANAGE_PROJECT_ASSIGNMENT.permissions,
		...MANAGE_WORKFLOW_COLUMN.permissions,
		...MANAGE_ALLOCATION.permissions,
		...MANAGE_SPRINTS.permissions,
		...MANAGE_PHASES.permissions,
	],
	dependingPermissionGroups: [APPROVE_ALLOCATION],
	description:
		'Ability to create new projects and access all project settings, including creation of phases & sprints. ' +
		'Ability to manage all resources from Schedule people and Project timeline.',
};

export const PROJECT_SCHEDULING_SHARE_CREATE = {
	name: 'Share Schedule Projects',
	permissions: [PERMISSION_TYPE.PROJECT_SCHEDULING_SHARE_CREATE, ...MANAGE_PROJECTS.permissions],
	description: 'Ability to share the Schedule Projects.',
};

export const MANAGE_PROGRAMS = () => {
	return {
		name: 'Manage ' + programsText(),
		permissions: [
			PERMISSION_TYPE.PROGRAMS_CREATE,
			PERMISSION_TYPE.PROGRAMS_UPDATE,
			PERMISSION_TYPE.PROGRAMS_DELETE,
			...MANAGE_PROJECTS.permissions,
		],
		description: `Ability to create new ${programsText()}, add/remove projects to a ${programText()} and manage projects`,
	};
};

export const SEE_MANAGE_RETAINER_PERIOD = {
	name: 'See and manage retainer periods',
	permissions: [
		PERMISSION_TYPE.RETAINER_PERIOD_READ,
		PERMISSION_TYPE.RETAINER_PERIOD_CREATE,
		PERMISSION_TYPE.RETAINER_PERIOD_UPDATE,
		PERMISSION_TYPE.RETAINER_PERIOD_DELETE,
		...VIEW_FINANCIAL_INFORMATION_REVENUE.permissions,
		...MANAGE_PROJECTS.permissions,
	],
	description: 'Access to the periods tab in projects and ability to create, edit & delete periods.',
};

export const VIEW_SISENSE_DASHBOARDS = {
	name: 'View advanced analytics dashboards',
	permissions: [PERMISSION_TYPE.SISENSE_READ],
	description: 'Ability to view Advanced Analytics dashboards shared with them.',
};

export const DESIGN_SISENSE_DASHBOARDS = {
	name: 'Design advanced analytics dashboards',
	permissions: [PERMISSION_TYPE.SISENSE_READ, PERMISSION_TYPE.SISENSE_CREATE],
	description: 'Ability to design and share Advanced Analytics dashboards.',
};

// Ticketing permission groups (ACC-495). Cumulative tiers per the ACC-415 matrix:
// VIEW ⊂ CREATE ⊂ ASSIGN ⊂ MANAGE ⊂ ADMIN. Each tier spreads the tier below it, so
// enabling a higher tier auto-includes the lower ones, and dependencyChecked() locks
// a lower row while a higher tier that contains it is enabled (same pattern as
// ACCESS_ALL_PROJECTS / READ_ALL_PROJECTS). Only rendered when ticketing is available
// (TICKETING module OR the tickets_enabled flag — see isTicketingAvailable).
export const TICKETS_VIEW_GROUP = {
	name: 'View tickets',
	permissions: [PERMISSION_TYPE.TICKETS_VIEW],
	description: 'Access to the Tickets area and ability to view tickets.',
};

export const TICKETS_CREATE_GROUP = {
	name: 'Create tickets',
	permissions: [PERMISSION_TYPE.TICKETS_CREATE, ...TICKETS_VIEW_GROUP.permissions],
	description: 'Ability to create tickets. Includes viewing tickets.',
};

export const TICKETS_ASSIGN_GROUP = {
	name: 'Assign tickets',
	permissions: [PERMISSION_TYPE.TICKETS_ASSIGN, ...TICKETS_CREATE_GROUP.permissions],
	description: 'Ability to assign and reassign tickets to others, including bulk assignment.',
};

export const TICKETS_MANAGE_GROUP = {
	name: 'Manage tickets',
	permissions: [PERMISSION_TYPE.TICKETS_MANAGE, ...TICKETS_ASSIGN_GROUP.permissions],
	description: "Ability to edit other people's tickets and broadly manage labels, links and status.",
};

export const TICKETS_ADMIN_GROUP = {
	name: 'Administer tickets',
	permissions: [PERMISSION_TYPE.TICKETS_ADMIN, ...TICKETS_MANAGE_GROUP.permissions],
	description: 'Full ticketing administration, including account-level ticket settings and the ticket Dashboard.',
};

// "Manage quotes" is a container row: it holds no permission of its own, only the union of its
// children's, so ticking it grants them all and it shows as checked only while they all are.
// Children carry `nested` so the permissions table indents them under the parent, and each covers
// one step of the quote lifecycle — create, read the audit, decide, send. Only rendered when quotes
// are available (quotes_builder flag AND OPPORTUNITIES module — see isQuotesEnabled).
export const CREATE_QUOTES = {
	name: 'Create quotes',
	permissions: [PERMISSION_TYPE.QUOTE_CREATE],
	description: 'Ability to create and publish a quote from the Baseline page.',
	nested: true,
};

export const VIEW_QUOTE_ACTIVITY = {
	name: 'View quote activity',
	permissions: [PERMISSION_TYPE.QUOTE_VIEW_ACTIVITY],
	description:
		'Ability to see the Quote Activity log on the Baseline page — who published, approved or declined a quote, when, and on whose behalf. Gated on a quote existing, not on its status.',
	nested: true,
};

export const APPROVE_DECLINE_REVISE_QUOTES = {
	name: 'Approve, decline, or revise quotes',
	permissions: [PERMISSION_TYPE.QUOTE_APPROVE_DECLINE_REVISE],
	description:
		'Ability to record an approve or decline decision against a quote on behalf of a client contact. Only available while the quote is Published or Sent — a decided quote cannot be decided again. Does not grant client self-service approval, which is a separate permission.',
	nested: true,
};

export const SEND_QUOTES = {
	name: 'Send quotes',
	permissions: [PERMISSION_TYPE.QUOTE_SEND],
	description: 'Ability to email a published quote to the client contacts on the project.',
	nested: true,
};

export const MANAGE_QUOTES = {
	name: 'Manage quotes',
	permissions: [
		...CREATE_QUOTES.permissions,
		...VIEW_QUOTE_ACTIVITY.permissions,
		...APPROVE_DECLINE_REVISE_QUOTES.permissions,
		...SEND_QUOTES.permissions,
	],
	description:
		'Full control over quotes. Enabling this grants everything nested beneath it; switch individual rows off to restrict a profile to part of it.',
};

// "Manage opportunities" is a container row: it holds no permission of its own, only the union of
// its children's, so ticking it grants them both and it shows as checked only while both are.
// Children carry `nested` so the permissions table indents them under the parent. Only rendered
// when opportunities are available (opportunities flag AND OPPORTUNITIES module — see
// isOpportunitiesEnabled).
export const CREATE_OPPORTUNITIES = {
	name: 'Create opportunities',
	permissions: [PERMISSION_TYPE.OPPORTUNITY_CREATE],
	description:
		'Ability to create a new opportunity from the top navigation, the Opportunities page and the client Opportunities tab. Additional to, not a replacement for, "Create projects" — both are required.',
	nested: true,
};

export const VIEW_OPPORTUNITIES_AS_SALESPERSON = {
	name: 'View opportunities as salesperson',
	permissions: [PERMISSION_TYPE.OPPORTUNITY_VIEW_AS_SALESPERSON],
	description:
		'Read-only visibility of the opportunities this person is the salesperson on, without being on the project team. Access disappears on its own once the deal leaves the Opportunity stage, and never grants edit rights.',
	nested: true,
};

export const MANAGE_OPPORTUNITIES = {
	name: 'Manage opportunities',
	permissions: [...CREATE_OPPORTUNITIES.permissions, ...VIEW_OPPORTUNITIES_AS_SALESPERSON.permissions],
	description:
		'Full control over opportunities. Enabling this grants everything nested beneath it; switch individual rows off to restrict a profile to part of it.',
};

export const ACCESS_BASELINES = {
	name: 'Access baselines',
	permissions: [PERMISSION_TYPE.BASELINE_ACCESS],
	description:
		'Create and edit Baselines, including phases and project dates on the Baseline, and view and insert Baseline ' +
		'templates. Shows revenue only. Does not grant cost or profit, the Budget, Periods, Invoices or Rate cards ' +
		'pages, or project financial settings. Not needed alongside either View financial information row, which ' +
		'already gives access to the Baseline.',
	nested: true,
	cy: 'permission-group-access-baselines',
};

// Neither row is part of either financial bundle. Shown only with the baseline_templates flag and the Baseline module,
// and hidden for Sage-restricted companies like the financial rows. The parent holds the union of its children's
// permissions, so while there is one child the two rows move together.
export const MANAGE_BASELINES = {
	name: 'Manage baselines',
	// Add every new child's permissions here by hand: the union is not derived from the nested rows.
	permissions: [...ACCESS_BASELINES.permissions],
	description: 'Access to Baselines. Enabling this grants everything nested beneath it.',
	cy: 'permission-group-manage-baselines',
};

// FOR-261. Deliberately NOT folded into the TICKETS_* tiers above: those nest
// (each contains the one below), and this is an orthogonal own-vs-others toggle —
// the same shape as MANAGE_COMMENTS_ALL. Deleting your OWN attachment needs no
// permission at all; this one only unlocks other people's files.
export const MANAGE_ATTACHMENTS_ALL = {
	name: "Delete another person's attachment",
	permissions: [PERMISSION_TYPE.ATTACHMENT_DELETE_ALL],
	description: 'Ability to delete attachments uploaded by other users on tickets.',
};

// Stream permission groups (SIG-18). View Stream is contained in the other two, so ticking either grants
// View Stream too, and dependencyChecked() locks the View row while one of them is held — the same
// cumulative pattern as the ticketing tiers. Only rendered while the Stream is available (STREAM module
// AND the stream_feature flag — see isStreamAvailable). Client and guest profiles never reach this
// editor, and service-company refuses these permissions on them.
export const STREAM_VIEW_GROUP = {
	name: 'View Stream',
	permissions: [PERMISSION_TYPE.STREAM_VIEW],
	description:
		"Access to the Stream on project and client pages: each source's title, date, participants and what it relates to.",
};

export const STREAM_CONTENT_MANAGE_GROUP = {
	name: 'Manage Ingested Content',
	permissions: [PERMISSION_TYPE.STREAM_CONTENT_MANAGE, ...STREAM_VIEW_GROUP.permissions],
	description:
		'Ability to read the full content of emails and transcripts in the Stream, and to mark sources secret. Includes viewing the Stream.',
};

export const STREAM_IMPORT_GROUP = {
	name: 'Import Transcripts',
	permissions: [PERMISSION_TYPE.STREAM_IMPORT, ...STREAM_VIEW_GROUP.permissions],
	description: 'Ability to import meeting transcripts into a project or client Stream. Includes viewing the Stream.',
};

const isNotSageRestricted = () => !hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED);
const hasTaskStatusPermission = () => hasFeatureFlag(FEATURE_FLAGS.TASK_STATUS_PERMISSION);
const isBaselineAccessShown = () =>
	hasFeatureFlag(FEATURE_FLAGS.BASELINE_TEMPLATES) && hasModule(MODULE_TYPES.BASELINE) && isNotSageRestricted();

// Every group the profile editor can render, in display order, each with the gate (if any) that
// decides whether this company sees it. One list, so a group added later cannot be rendered yet
// left out of the coverage checks in PermissionsUtil — two hand-kept lists would reintroduce the
// orphaned-permission bug for it.
// A function so that ACCESS_ALL_PROJECTS / MANAGE_PROGRAMS re-read their module-dependent labels.
const PERMISSION_GROUP_CATALOGUE = () => [
	{group: VIEW_FINANCIAL_INFORMATION, isShown: isNotSageRestricted},
	{group: VIEW_FINANCIAL_INFORMATION_REVENUE, isShown: isNotSageRestricted},
	{group: MANUAL_REVENUE_RECOGNITION, isShown: isNotSageRestricted},
	// Beside the financial rows, since an admin weighs it against them. Parent first — the table indents the row
	// after it, which is marked `nested`.
	...[MANAGE_BASELINES, ACCESS_BASELINES].map(group => ({group, isShown: isBaselineAccessShown})),
	{group: ACCESS_ALL_PROJECTS()},
	{group: READ_ALL_PROJECTS},
	{group: PROJECT_SCHEDULING_SHARE_CREATE},
	{group: MANAGE_PROGRAMS(), isShown: () => hasModule(MODULE_TYPES.PROGRAMS)},
	{group: MANAGE_PROJECTS},
	{group: MANAGE_PHASES},
	//SEE_MANAGE_BASELINE,
	{group: MANAGE_SPRINTS},
	{group: MANAGE_WORKFLOW_COLUMN},
	// Parent must come first — the table indents whatever follows it and is marked `nested`.
	// "Change task status" sits between Edit and Delete but is the only child behind a flag, shown on
	// exactly the flag that enforces it: a permission is never enforced without a row to grant it
	// back (which would strand an admin whose profile has lost it).
	{group: MANAGE_TASKS},
	{group: CREATE_TASKS},
	{group: EDIT_TASKS},
	{group: CHANGE_TASK_STATUS, isShown: hasTaskStatusPermission},
	{group: DELETE_TASKS},
	{group: APPROVE_ALLOCATION, isShown: () => hasModule(MODULE_TYPES.ALLOCATION_APPROVE)},
	//MANAGE_ALLOCATION,
	//MANAGE_PROJECT_ASSIGNMENT,
	//SCHEDULING_ACCESS,
	//CREATE_UPDATE_CLIENT,
	{group: SEE_MANAGE_INSIGHTS, isShown: isNotSageRestricted},
	//SEE_MANAGE_INVOICES,
	{group: SEE_MANAGE_RETAINER_PERIOD},
	{group: MANAGE_TIME_REGISTRATIONS_ALL, isShown: isNotSageRestricted},
	{group: MANAGE_TIME_OFF, isShown: isNotSageRestricted},
	{group: CREATE_PERSON},
	{group: MANAGE_COMMENTS_ALL},
	{group: VIEW_SISENSE_DASHBOARDS},
	{group: DESIGN_SISENSE_DASHBOARDS},
	{group: ALL_TIMELINES_VIEW_ONLY},
	{group: PEOPLE_SCHEDULE_VIEW_ONLY},
	{group: DEMAND_VIEW_ONLY},
	{group: CAPACITY_OVERVIEW_VIEW_ONLY},
	// Never gated: the AI entry points are hidden without this permission, so the row must always be
	// there to grant it back.
	{group: AI_ACCESS_GROUP},
	...[
		TICKETS_VIEW_GROUP,
		TICKETS_CREATE_GROUP,
		TICKETS_ASSIGN_GROUP,
		TICKETS_MANAGE_GROUP,
		TICKETS_ADMIN_GROUP,
		MANAGE_ATTACHMENTS_ALL,
	].map(group => ({group, isShown: isTicketingAvailable})),
	...[STREAM_VIEW_GROUP, STREAM_CONTENT_MANAGE_GROUP, STREAM_IMPORT_GROUP].map(group => ({
		group,
		isShown: isStreamAvailable,
	})),
	// Parent must come first — the table indents whatever follows it and is marked `nested`.
	...[MANAGE_QUOTES, CREATE_QUOTES, VIEW_QUOTE_ACTIVITY, APPROVE_DECLINE_REVISE_QUOTES, SEND_QUOTES].map(group => ({
		group,
		isShown: isQuotesEnabled,
	})),
	// Parent must come first — the table indents whatever follows it and is marked `nested`.
	...[MANAGE_OPPORTUNITIES, CREATE_OPPORTUNITIES, VIEW_OPPORTUNITIES_AS_SALESPERSON].map(group => ({
		group,
		isShown: isOpportunitiesEnabled,
	})),
];

// Every group, whatever the company's modules and flags. For reasoning about what a profile holds —
// a gate hides a row, it does not revoke what the profile was granted. Never use it to decide what to
// render; that is PERMISSION_GROUPS.
export const ALL_PERMISSION_GROUPS = () => PERMISSION_GROUP_CATALOGUE().map(({group}) => group);

// The groups this company's profile editor renders.
export const PERMISSION_GROUPS = () =>
	PERMISSION_GROUP_CATALOGUE()
		.filter(({isShown}) => !isShown || isShown())
		.map(({group}) => group);
